import { ProduktKarticka } from '@/components/druhaJakost/ProduktKarticka';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { apiFetch } from '@/lib/apiFetch';
import {
  type Zalozena,
  cenaPoSleve,
  errMsg,
  fmtKc,
  parseSleva,
  popisAkce,
} from '@/lib/druhaJakost';
import { useEffect, useState } from 'react';

const fmtPct = (n: number): string => String(n).replace('.', ',');

type Varianta = {
  to: string;
  size: string;
  orig_price: number | null;
  base_price: number | null;
  orig_action: boolean;
  /** Za kolik se 2. jakost prodává teď v Shoptetu (null = ještě tam není). */
  current_price?: number | null;
  /** Skutečná sleva teď proti prodejní ceně originálu, v %. */
  current_pct?: number | null;
  orig_action_raw: {
    price?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
  } | null;
};

/**
 * Sleva po velikostech u už založené 2. jakosti — např. větší kaz na 104.
 * Prázdné políčko = platí sleva produktu. Uložení přepíše ceník v Shoptetu.
 */
export function CenyVelikostiModal({
  item,
  onClose,
  onDone,
}: {
  item: Zalozena;
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => void;
}) {
  useEscapeKey(onClose);
  const [variants, setVariants] = useState<Varianta[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [sleva, setSleva] = useState(item.shoptet_cz_sleva ? String(item.shoptet_cz_sleva) : '');
  const [vel, setVel] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(item.shoptet_cz_sleva_vel ?? {}).map(([k, v]) => [k, String(v)]),
    ),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = `code=${encodeURIComponent(item.original_code)}&druha_id=${item.id}`;
    apiFetch<{ variants: Varianta[] }>(`/api/druha-jakost/shoptet-cz/nahled?${q}`)
      .then((d) => setVariants(d.variants))
      .catch((e: unknown) => setLoadError(errMsg(e, 'Ceny ze Shoptetu se nenačetly.')));
  }, [item.id, item.original_code]);

  const zaklad = parseSleva(sleva);
  // Rozpětí skutečných slev v Shoptetu („15 %" nebo „14,3–20 %").
  const ted = (variants ?? []).map((v) => v.current_pct).filter((p): p is number => p != null);
  const souhrn =
    ted.length === 0
      ? ''
      : Math.min(...ted) === Math.max(...ted)
        ? `${fmtPct(Math.min(...ted))} %`
        : `${fmtPct(Math.min(...ted))}–${fmtPct(Math.max(...ted))} %`;
  // Neplatné číslo u velikosti blokuje uložení, ať se tiše nepoužije sleva produktu.
  const spatne = Object.values(vel).some((v) => v.trim() !== '' && parseSleva(v) === null);
  // Bez slevy produktu se mění jen vyplněné velikosti, ostatní zůstanou v Shoptetu, jak jsou.
  const jenVelikosti = sleva.trim() === '';
  const vyplnene = Object.values(vel).some((v) => parseSleva(v) !== null);
  const lzeUlozit = jenVelikosti ? vyplnene : zaklad !== null;

  async function save() {
    if (!lzeUlozit || spatne) return;
    const sleva_vel: Record<string, number> = {};
    for (const [code, v] of Object.entries(vel)) {
      const p = parseSleva(v);
      if (p !== null && (jenVelikosti || p !== zaklad)) sleva_vel[code] = p;
    }
    setSaving(true);
    try {
      const res = await apiFetch<{ warning: string | null }>(
        `/api/druha-jakost/${item.id}/shoptet-cz/dorovnat`,
        {
          method: 'POST',
          body: jenVelikosti
            ? { sleva_vel, jen_velikosti: true }
            : { sleva_pct: zaklad, sleva_vel },
        },
      );
      onDone(
        res.warning ? `Uloženo, ale: ${res.warning}` : `${item.new_code}: ceny uloženy do Shoptetu`,
        !res.warning,
      );
      onClose();
    } catch (e: unknown) {
      onDone(errMsg(e, 'Uložení cen selhalo.'), false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-3xl my-10 bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">Ceny po velikostech · {item.new_code}</h2>
        <div className="grid gap-4 sm:grid-cols-2 mt-3 mb-3">
          <ProduktKarticka
            label="Originál (1. jakost)"
            code={item.original_code}
            name={item.original_eshop?.name ?? ''}
            image={item.original_eshop?.image}
            url={item.original_eshop?.url}
          />
          <ProduktKarticka
            label="2. jakost"
            code={item.new_code}
            name={item.new_eshop?.name ?? item.new_name ?? ''}
            image={item.new_eshop?.image}
            url={item.new_eshop?.url}
          />
        </div>
        <p className="text-sm text-text-secondary pb-4 mb-4 border-b border-border">
          Akční cena = akční cena originálu ({item.original_code}) − sleva. Prázdné políčko u
          velikosti = sleva produktu. Bez slevy produktu se změní jen vyplněné velikosti, ostatní
          zůstanou beze změny.
        </p>

        <label className="flex items-center gap-2 mb-4 text-sm text-text" htmlFor="sleva-produkt">
          Sleva produktu
          <input
            id="sleva-produkt"
            type="text"
            inputMode="decimal"
            value={sleva}
            onChange={(e) => setSleva(e.target.value)}
            className="w-20 rounded-lg border border-border bg-surface px-2 py-1.5 text-right focus:outline-none focus:border-primary"
          />
          %
        </label>

        {souhrn && (
          <p className="mb-4 rounded-lg bg-bg px-3 py-2 text-sm text-text">
            Teď v Shoptetu: sleva <b>{souhrn}</b> proti {item.original_code} (prodejní cena 2.
            jakosti proti prodejní ceně originálu).
          </p>
        )}
        {loadError && <p className="text-sm text-danger">{loadError}</p>}
        {!variants && !loadError && <p className="text-sm text-text-muted">Načítám ze Shoptetu…</p>}
        {variants && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-text-muted">
                <th className="py-2 pr-3">Velikost</th>
                <th className="py-2 pr-3 text-right">Cena</th>
                <th className="py-2 pr-3 text-right">Akční originálu</th>
                <th className="py-2 pr-3 text-right">Teď v Shoptetu</th>
                <th className="py-2 pr-3 text-right">Sleva %</th>
                <th className="py-2 text-right">Akční 2. jakost</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => {
                const vlastni = parseSleva(vel[v.to] ?? '');
                const pct = vlastni ?? zaklad;
                return (
                  <tr key={v.to} className="border-b border-border-light">
                    <td className="py-1.5 pr-3 text-text">
                      {v.size || v.to}
                      <span className="block text-xs font-mono text-text-muted">{v.to}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-text-muted">
                      {v.orig_price !== null ? fmtKc(v.orig_price) : '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-text-muted">
                      {popisAkce(v)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {v.current_price != null ? (
                        <>
                          <span className="text-text">{fmtKc(v.current_price)}</span>
                          {v.current_pct != null && (
                            <span
                              className={`block text-xs ${
                                pct && Math.abs(v.current_pct - pct) >= 1
                                  ? 'text-warning font-semibold'
                                  : 'text-text-muted'
                              }`}
                            >
                              −{fmtPct(v.current_pct)} %
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Sleva ${v.size || v.to}`}
                        value={vel[v.to] ?? ''}
                        placeholder={zaklad ? String(zaklad) : ''}
                        onChange={(e) => setVel((m) => ({ ...m, [v.to]: e.target.value }))}
                        className={`w-16 rounded border px-2 py-1 text-right text-text bg-surface focus:outline-none focus:border-primary ${
                          vlastni !== null && vlastni !== zaklad
                            ? 'border-warning font-semibold'
                            : 'border-border'
                        }`}
                      />
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-text">
                      {v.base_price !== null && pct ? fmtKc(cenaPoSleve(v.base_price, pct)) : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {spatne && <p className="mt-3 text-sm text-danger">Sleva musí být číslo mezi 0 a 100 %.</p>}
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-5 py-2.5 text-sm text-text-secondary hover:bg-surface-hover cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || !variants || !lzeUlozit || spatne}
            className="rounded-lg bg-action px-5 py-2.5 text-sm font-semibold text-white hover:bg-action-dark disabled:opacity-50 cursor-pointer"
          >
            {saving ? 'Ukládám…' : 'Uložit do Shoptetu'}
          </button>
        </div>
      </div>
    </div>
  );
}
